import { create } from 'zustand';
import { apiFetch } from './authStore';

/**
 * Shared admin org-scope: which organization an admin is currently viewing.
 *
 * Persists to the SAME localStorage key the Manager view already uses
 * (`g1.manager.selectedOrgId`), so the selection stays consistent across every
 * org-scoped screen via navigation — without refactoring Manager's internals.
 * The relay honors the selection via `?orgId=` (resolveOrgId, admin-only).
 * Admins only; non-admin managers always scope to their own org.
 */
const STORAGE_KEY = 'g1.manager.selectedOrgId';

export interface OrgOption {
  id: string;
  name: string;
}

interface OrgScopeState {
  selectedOrgId: string;
  orgs: OrgOption[];
  orgsLoaded: boolean;
  setSelectedOrgId: (id: string) => void;
  ensureOrgs: () => Promise<void>;
}

function loadStored(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export const useOrgScope = create<OrgScopeState>((set, get) => ({
  selectedOrgId: loadStored(),
  orgs: [],
  orgsLoaded: false,
  setSelectedOrgId: (id) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
    set({ selectedOrgId: id });
  },
  ensureOrgs: async () => {
    if (get().orgsLoaded) return;
    try {
      const res = await apiFetch('/api/admin/orgs');
      const j = await res.json().catch(() => ({}));
      const orgs: OrgOption[] = Array.isArray(j.orgs)
        ? j.orgs.map((o: { id: string; name: string }) => ({ id: o.id, name: o.name }))
        : [];
      set({ orgs, orgsLoaded: true });
    } catch {
      set({ orgsLoaded: true });
    }
  },
}));
